import { useState, type ReactNode } from 'react';

// Lista de personas disponibles de UN turno en los formularios de Nueva
// solicitud (admin y cliente). Con 70+ personas la lista plana era inmanejable
// (pedido de Fernando 14/07/2026): buscador por nombre o cédula, altura fija
// con scroll y plegado por turno para trabajar cómodo con varios turnos.

export interface PersonaDisponible {
  id: string;
  nombre: string;
  apellido: string;
  documento: string | null;
  calificacion: number;
  prioridad: number;
  es_principal: boolean;
  disponible: boolean;
  motivo_no_disponible: string | null;
}

interface Props {
  personas: PersonaDisponible[];
  /** Elegidas a dedo en ESTE turno. */
  sel: string[];
  /** Cupo del turno (tope de elegidas). */
  cantidad: number;
  /** Elegidas en cualquier turno del evento (una persona = un turno). */
  selGlobal: Set<string>;
  onToggle: (personaId: string) => void;
}

function normalizar(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export function ListaPersonasTurno({ personas, sel, cantidad, selGlobal, onToggle }: Props): ReactNode {
  const [busqueda, setBusqueda] = useState('');
  const [cerrada, setCerrada] = useState(false);
  const [ocultarNoDisp, setOcultarNoDisp] = useState(false);

  const terminos = normalizar(busqueda.trim()).split(/\s+/).filter(Boolean);
  const qDigitos = busqueda.replace(/\D/g, '');
  const hayBusqueda = terminos.length > 0;

  const visibles = personas.filter((p) => {
    if (ocultarNoDisp && !p.disponible) return false;
    if (!hayBusqueda) return true;
    const nombreCompleto = normalizar(`${p.nombre} ${p.apellido}`);
    if (terminos.every((t) => nombreCompleto.includes(t))) return true;
    return qDigitos.length > 0 && (p.documento ?? '').replace(/\D/g, '').includes(qDigitos);
  });
  const elegidas = personas.filter((p) => sel.includes(p.id));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          className="flex items-center gap-1.5 text-xs font-bold text-neutro-700 hover:text-primario-500 transition-colors"
          onClick={() => setCerrada(!cerrada)}
        >
          <span className="text-[10px]">{cerrada ? '▶' : '▼'}</span>
          Personas
          <span className="font-semibold text-neutro-400">
            ({sel.length}/{cantidad} elegida{sel.length !== 1 ? 's' : ''})
          </span>
        </button>
        {!cerrada && (
          <button
            type="button"
            className="text-xs font-medium text-primario-400 hover:text-primario-500 transition-colors"
            onClick={() => setOcultarNoDisp(!ocultarNoDisp)}
          >
            {ocultarNoDisp ? 'Mostrar todos' : 'Ocultar no disponibles'}
          </button>
        )}
      </div>

      {cerrada ? (
        <p className="mt-1.5 text-xs text-neutro-500">
          {elegidas.length > 0
            ? elegidas.map((p) => `${p.apellido}, ${p.nombre}`).join(' · ')
            : 'Nadie elegido a dedo: el sistema convoca solo.'}
        </p>
      ) : (
        <>
          <input
            type="search"
            className="campo mt-2 mb-2 text-sm"
            placeholder="Buscar por nombre o cédula…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          <ul className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
            {visibles.map((p) => {
              const elegidaAqui = sel.includes(p.id);
              const bloqueada = !p.disponible || (!elegidaAqui && (selGlobal.has(p.id) || sel.length >= cantidad));
              return (
                <li key={p.id} className={`rounded-lg border px-3 py-2.5 text-sm transition-all ${!bloqueada || elegidaAqui ? elegidaAqui ? 'bg-primario-100 border-primario-400 text-primario-700' : 'bg-white border-contorno-var hover:border-primario-200 text-neutro-700' : 'bg-neutro-100 border-contorno-var opacity-50'}`}>
                  <label className="flex cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-neutro-300 text-primario-400 focus:ring-primario-400"
                      checked={elegidaAqui}
                      disabled={bloqueada}
                      onChange={() => onToggle(p.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{p.apellido}, {p.nombre}</p>
                      <p className="text-xs text-neutro-400">
                        {p.documento ? `CI: ${p.documento}` : ''}
                        {p.prioridad ? ` · Pri: ${p.prioridad}` : ''}
                        {p.es_principal ? ' · Principal' : ''}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs">
                        {'★'.repeat(Math.round(p.calificacion))}<span className="text-neutro-200">{'★'.repeat(5 - Math.round(p.calificacion))}</span>
                      </p>
                      {!p.disponible && (
                        <p className="text-xs text-estado-error">{p.motivo_no_disponible}</p>
                      )}
                      {p.disponible && !elegidaAqui && selGlobal.has(p.id) && (
                        <p className="text-xs text-neutro-400">Ya elegida en otro turno</p>
                      )}
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
          {visibles.length === 0 && (
            <p className="mt-1 text-xs text-neutro-400">
              {hayBusqueda ? 'Nadie coincide con la búsqueda.' : 'No hay personas disponibles para este turno.'}
            </p>
          )}
        </>
      )}
    </div>
  );
}
